import { useState, useRef, KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const PRESET_TAGS = [
  // Content Pillars
  'educational',
  'entertainment',
  'inspirational',
  'community',
  'news',
  'thought-leadership',
  'bts',

  // Post Format
  'reel',
  'carousel',
  'thread',
  'poll',
  'meme',
  'infographic',
  'testimonial',
  'ugc',

  // Campaign / Intent
  'promotional',
  'campaign',
  'launch',
  'giveaway',
  'partnership',
  'sponsored',
  'seasonal',

  // Funnel Stage
  'awareness',
  'conversion',
  'retention',

  // Misc
  'random',
];

interface TagSelectorProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  disabled?: boolean;
}

export function TagSelector({ tags, onChange, disabled = false }: TagSelectorProps) {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const addTag = (tag: string) => {
    const normalized = tag.trim().toLowerCase();
    if (!normalized || tags.map((t) => t.toLowerCase()).includes(normalized)) return;
    onChange([...tags, tag.trim()]);
  };

  const removeTag = (tag: string) => {
    onChange(tags.filter((t) => t !== tag));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(inputValue);
      setInputValue('');
    } else if (e.key === 'Backspace' && inputValue === '' && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  const togglePreset = (preset: string) => {
    if (disabled) return;
    const alreadyAdded = tags.map((t) => t.toLowerCase()).includes(preset.toLowerCase());
    if (alreadyAdded) {
      onChange(tags.filter((t) => t.toLowerCase() !== preset.toLowerCase()));
    } else {
      onChange([...tags, preset]);
    }
  };

  return (
    <div className="space-y-2">
      {/* Preset tag pills */}
      <div className="flex flex-wrap gap-1.5">
        {PRESET_TAGS.map((preset) => {
          const isSelected = tags.map((t) => t.toLowerCase()).includes(preset.toLowerCase());
          return (
            <button
              key={preset}
              type="button"
              onClick={() => togglePreset(preset)}
              disabled={disabled}
              className={`px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors ${
                isSelected
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground border-border hover:border-foreground/40 hover:text-foreground'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {preset}
            </button>
          );
        })}
      </div>

      {/* Selected custom tags + input */}
      <div
        className="flex flex-wrap gap-1.5 min-h-9 rounded-md cursor-text"
        onClick={() => inputRef.current?.focus()}
      >
        {tags.map((tag) => (
          <Badge
            key={tag}
            variant="secondary"
            className={`flex items-center gap-1 pr-1 text-xs font-normal ${disabled && 'pr-2.5'} max-h-[25px]`}
          >
            {tag}
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeTag(tag);
                }}
                className="ml-0.5 rounded-full hover:bg-muted-foreground/20 transition-colors"
                aria-label={`Remove tag ${tag}`}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        ))}
        {!disabled && (
          <>
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={() => {
                if (inputValue.trim()) {
                  addTag(inputValue);
                  setInputValue('');
                }
              }}
              placeholder={tags.length === 0 ? 'Add a tag… (Enter or comma to add)' : ''}
              // className="border-0 shadow-none p-0 h-auto min-w-[140px] flex-1 focus-visible:ring-0 text-xs"
              className={cn(
                'flex items-center gap-1 focus-within:ring-1 focus-within:ring-ring resize-none text-sm',
                'w-full text-[12px] rounded-[10px] outline-none transition-all duration-150 shadow-social-card',
                'h-[40px] px-3 py-3 !font-mono',
                'text-primary',
                'bg-background  placeholder-gray-500',
              )}
            />
            <span className="!font-mono text-[10px]">Write tags and hit enter</span>
          </>
        )}
      </div>
    </div>
  );
}
